import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface ThankYouSettingsValues {
    thanksMessage: string;
}

const ThankYouSettings = () => {
    const { success, error } = useToast();
    const { settings, accountId } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();

    const thanksMessageDefault = settings.find(item => item.code === Settings.ThanksMessage.Code);

    const methods = Form.useGivebutterForm<ThankYouSettingsValues>({
        defaultValues: {
            thanksMessage: thanksMessageDefault?.value || '',
        },
    });

    const {
        control,
        formState: { isDirty },
        path,
        handleSubmit,
        reset,
    } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        ThankYouSettingsValues
    >({
        mutationFn: ({ thanksMessage }) => {
            return updateAccount(accountId, {
                settings: [
                    {
                        code: Settings.ThanksMessage.Code,
                        value: thanksMessage || null,
                        active: !!thanksMessage,
                        name: 'thank_you_message',
                        type: 'text',
                    },
                ],
            });
        },
        onSuccess: (_response, values) => {
            success('Thank You Message saved successfully!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            reset(values);
            invalidateUserData();
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <Box className="flex flex-col gap-8 p-8">
            <div>
                <h3 className="text-2xl font-medium">Thank You Message</h3>
                <p>
                    This message will appear in the email confirmation supporters receive. You can
                    override this message and send yourself a test by visiting the Settings tab on
                    individual campaigns.
                </p>
            </div>
            <Form.Provider {...methods}>
                <form
                    className="flex flex-col items-start gap-6"
                    onSubmit={handleSubmit(values => submit(values))}
                >
                    <Form.Container>
                        <Form.Label name={path('thanksMessage')} className="hidden">
                            Thank You Message
                        </Form.Label>

                        <Wysiwyg.Root>
                            <UseFormWysiwyg
                                name={path('thanksMessage')}
                                control={control}
                                inline={false}
                                init={{
                                    statusbar: false,
                                    plugins: ['autolink', 'link', 'help', 'powerpaste'],
                                    toolbar: 'bold italic link',
                                    content_style: Wysiwyg.Constants.APP_FONT,
                                    menubar: false,
                                }}
                            />
                        </Wysiwyg.Root>
                    </Form.Container>

                    <Form.SubmitButton isSubmitting={isLoading} disabled={!isDirty}>
                        Save
                    </Form.SubmitButton>
                </form>
            </Form.Provider>
        </Box>
    );
};

export { ThankYouSettings };
